import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { REACT_JS } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const ReactJSSeo = formatSeoConfig({
  title: 'React.js for Kids: Build Web Projects Online',
  description:
    'React.js classes for kids, led by expert instructors. Build real-world web projects with a structured, hands-on curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/reactjs-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v1.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <h3 className="font-600 text-lg lg:text-xl">
      Why Should Kids Learn React.js?
    </h3>
    <p>
      React.js is one of the most powerful and in-demand tools in modern web
      development and learning it early gives kids a huge advantage in the
      digital world. It helps them move beyond simple coding to building real,
      interactive web applications that look and feel like the ones they use
      every day.
    </p>
    <p>Here’s why React.js is great for young learners</p>
    <ul>
      <li>
        <strong>Industry-Relevant Skills:</strong> React.js is used by companies
        like Netflix, Instagram, and Facebook. Kids who learn it early gain
        experience with professional-grade technology.
      </li>
      <li>
        <strong>Teaches Reusable Thinking:</strong> React introduces components
        — small, reusable building blocks that teach children how to structure,
        organize, and optimize their code efficiently.
      </li>
      <li>
        <strong>Strengthens Logical & Analytical Thinking:</strong> Kids learn
        how to break complex problems into smaller parts, improving their
        problem-solving and computational reasoning.
      </li>
      <li>
        <strong>Fuels Creativity:</strong> React makes it fun to design dynamic,
        responsive, and colorful user interfaces. Kids can see their ideas come
        alive instantly in the browser.
      </li>
      <li>
        <strong>Prepares for Future Tech Careers:</strong> Knowing React.js
        opens doors to advanced topics like Next.js, app development, and
        full-stack programming, giving kids a solid head start in computer
        science.
      </li>
      <li>
        <strong>Connects to Real-World Coding:</strong> React bridges the gap
        between beginner coding and professional web development, helping kids
        understand how real apps and websites are built.
      </li>
    </ul>
    <p>
      By learning React.js, children don’t just code, they create, innovate, and
      think like real developers. It’s the perfect next step for kids who have
      mastered JavaScript and want to explore how modern web apps work.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain React.js to Kids?
    </h3>
    <p>
      Imagine building a big Lego castle — 🏰 — but instead of creating
      everything from scratch every time, you use smaller Lego pieces (like
      doors, windows, and towers) again and again to build something new.
    </p>
    <p>That’s exactly how React.js works!</p>
    <p>
      React.js is a special tool that helps kids build websites and apps using
      small, reusable pieces of code called components. Each component has a job
      just like a button, a picture, or a text box, and when you combine them,
      you get a complete web page or app that’s smart and interactive.
    </p>
    <p>Here’s how kids can think of it 👇</p>
    <ul>
      <li>
        🧱 <strong>Components = Lego Blocks:</strong> Each block does one thing
        — but together, they build something amazing.
      </li>
      <li>
        💻 <strong>React = Builder’s Toolkit:</strong> It helps organize and
        reuse code so websites run faster and smoother.
      </li>
      <li>
        🎮 <strong>Results = Instant Fun:</strong> When kids make changes in
        React, the screen updates right away — like magic!
      </li>
    </ul>
    <p>Fun examples of what kids can build with React.js:</p>
    <ul>
      <li>
        🧩 <strong>Quiz App:</strong> A quiz app that shows questions and
        scores.
      </li>
      <li>
        🎨 <strong>Color Picker:</strong> A color picker that changes background
        shades.
      </li>
      <li>
        📝 <strong>To-Do List:</strong> A to-do list that updates instantly when
        you add or remove tasks.
      </li>
      <li>
        📊 <strong>Mini Dashboard:</strong> A mini dashboard that displays live
        data.
      </li>
    </ul>
    <p>
      React makes coding feel like building, creating, and playing all at once.
      It helps kids understand how professional apps and websites are built, one
      component at a time, while keeping learning fun and hands-on. 🚀
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      What Does React.js Stand For?
    </h3>
    <p>
      Unlike some programming names, React.js isn’t an acronym, it doesn’t stand
      for anything. The name “React” comes from what it helps websites and apps
      do best: react quickly to what users do on the screen.
    </p>
    <p>
      When you click a button, type in a box, or scroll through a feed, React.js
      makes sure the website responds instantly without needing to reload the
      entire page. That’s why apps like Instagram, Netflix, and Facebook use
      React, it keeps everything fast, smooth, and interactive.
    </p>
    <p>Here’s a simple way to explain it</p>
    <ul>
      <li>
        ⚡ <strong>“React”</strong> means responding in real time — just like
        your favorite apps do.
      </li>
      <li>
        💡 <strong>“.js”</strong> stands for JavaScript, the programming
        language React is built on.
      </li>
      <li>
        🌐 <strong>Together, React.js</strong> means “React with JavaScript” — a
        smart tool that helps coders create modern, dynamic web pages easily.
      </li>
    </ul>
    <p>
      So, when kids learn React.js, they’re not just learning another coding
      language, they’re learning how to build apps that react and come alive,
      just like the websites they love to use every day!{' '}
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      An Easy Way to Understand React.js for Kids
    </h3>
    <p>
      Imagine you’re creating your favorite game or app, every button, score
      counter, and image you see on the screen is made of smaller pieces that
      work together.
    </p>
    <p>
      React.js helps you build those pieces one by one and connect them so
      everything works perfectly.
    </p>
    <p>Here’s an easy way to think about it 👇</p>
    <ul>
      <li>
        🧱 <strong>React Components = Building Blocks:</strong> Just like Lego,
        you can reuse the same pieces (like a button, a card, or a message box)
        to create big, amazing projects without starting over.
      </li>
      <li>
        ⚡ <strong>React Updates Instantly:</strong> When you click a button or
        type something, React changes only what’s needed — no refreshing the
        page! It’s fast, smooth, and feels like magic.
      </li>
      <li>
        🎨 <strong>React + HTML + CSS = Dynamic Websites:</strong> React uses
        JavaScript to make your HTML (structure) and CSS (design) move, change,
        and interact — helping you create web apps that look alive!
      </li>
      <li>
        🚀 <strong>React Helps You Think Like a Creator:</strong> You don’t just
        consume apps — you learn how to build them. Kids discover how real
        websites like YouTube or Spotify are made, and even create their own
        versions step by step.
      </li>
    </ul>
    <p>
      React.js makes learning web development fun, visual, and incredibly
      rewarding. With each project, kids see their ideas come alive by proving
      that creativity + code = endless possibilities.
    </p>
  </div>
)

const ReactjsPage = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...ReactJSSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[REACT_JS].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn React.js with Top AI & Coding Course Instructors for Kids and Teens"
        description="Step into the fascinating world of React.js, where coding becomes a canvas for imagination and innovation! With Codingal’s interactive curriculum, students bring their ideas to life through animations, visuals, and interactive web projects  all created using simple lines of HTML, CSS, and React.js. In every session, learners use React.js to make web pages move, respond, and react,  building creative designs, interactive stories, and dynamic effects. As they experiment with loops, functions, events, and logic, kids learn the fundamentals of programming in a fun, visual, and hands-on way. Each project introduces essential coding concepts such as variables, conditional statements, functions, and event handling, while encouraging creativity and problem-solving. From creating a Fireworks Animation or an Interactive Quiz to building a Mini Game, students learn how React.js powers the interactive web experiences they see every day. As students progress, they transition from visual coding platforms like Scratch and Play Lab to text-based environments such as Replit, p5.js, or CodePen. They learn how HTML and CSS work together with React.js to design and style complete web pages,  preparing them for real-world web development. Perfect for kids aged 8–14, Codingal’s React.js lessons build a strong foundation in programming, boost creative confidence, and make learning code as exciting as playing a game!"
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={REACT_JS}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={REACT_JS} />
          <CourseFeatures
            courseTitle={REACT_JS}
            title={<Trans>Engaging React.js classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={REACT_JS}
            title="Our React.js class curriculum"
            description={
              <>
                In Codingal’s Grandmaster AI & Coding program, students
                kickstart their React.js journey through fun, creative, and
                hands-on web development projects. They begin with an
                Introduction to React, learning how this powerful JavaScript
                library helps developers build dynamic and interactive web pages
                using small, reusable building blocks called components.
                <br />
                Students first explore the fundamentals of programming,
                JavaScript syntax, variables, sequencing, and logic while using
                HTML and CSS to design the structure and style of their web
                pages. Then, they learn how React renders HTML efficiently using
                the virtual DOM, showing kids how modern websites display
                information instantly without reloading the page.
                <br />
                Each lesson introduces key React.js concepts such as events,
                props, and state, helping students create interactive apps that
                respond to user actions in real time. They practice using events
                in React (like clicks, hovers, and keypresses) to trigger
                animations and functions. With props, they learn how to pass
                data between components, and with state, they manage dynamic
                content, like updating scores in a game or displaying messages
                on a web app.
                <br />
                As students progress, they explore more advanced programming
                topics such as arrays, methods, and asynchronous JavaScript
                (Async & APIs), learning how data is fetched, displayed, and
                updated on modern websites. They also understand how React
                handles component lifecycles, JSON data, and callback functions,
                giving them a deeper insight into real-world web app
                development.
                <br />
                By the end of the course, kids build a solid foundation in
                React.js, JavaScript, HTML, and CSS, capable of designing
                interactive websites, animations, and functional web
                applications.
                <br />
                Perfect for Grades 4–8, this curriculum empowers kids to think
                logically, code creatively, and solve problems like real
                developers, preparing them for advanced studies in web
                development, app design, and future STEM opportunities.
              </>
            }
          />
          <Activities courseName={REACT_JS} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning
                  React.js a truly supported experience, especially through our
                  virtual React.js lessons that connect kids with top educators
                  worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The Best Place for Kids to Learn the Basics and Fundamentals of
                React.js
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we provide the ideal environment for kids to
                  master React.js, a powerful JavaScript library used by
                  professionals to build modern, dynamic web applications. Our
                  curriculum combines step-by-step lessons, hands-on coding, and
                  live expert guidance to help children learn React.js with
                  confidence and creativity. Whether it’s building a simple app,
                  rendering HTML elements dynamically, or managing data with
                  state and props, kids discover how to bring their ideas to
                  life with real-world coding skills.
                </span>
              </Trans>
            }
            courseName={REACT_JS}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters React.js through a
                  STEM.org–accredited program built on proven educational
                  frameworks like Bloom’s Taxonomy, BIDE, and STEAM. Our
                  curriculum is aligned with Common Core, state standards, AP
                  Computer Science goals, and the K–12 Computer Science
                  Framework—ensuring learning that’s both age-appropriate and
                  college-ready. Through hands-on, project-based learning,
                  students create real-world apps like temperature converters
                  and scientific calculators, applying math, science, and
                  engineering logic to solve everyday problems. Backed by
                  STEM.org’s rigorous audits, every React.js lesson lays a
                  strong, future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your React.js Certificate"
            description={
              <>
                In Codingal’s Grandmaster React.js module, students take their
                web development skills to the next level by building
                interactive, real-world applications using React.js, JavaScript,
                HTML, and CSS. Each project helps kids apply what they’ve
                learned — from managing state and props to rendering dynamic
                components and handling user events with confidence.
                <br />
                Students start by creating simple, reusable UI components and
                gradually progress to developing complete mini web applications,
                such as a to-do app, quiz game, or color picker dashboard. Along
                the way, they gain hands-on experience using React hooks, APIs,
                and asynchronous data handling, just like real developers.
                <br />
                Upon successful completion, students earn the STEM.org Certified
                AI & Coding Grandmaster Certificate, a prestigious credential
                recognized globally. This certificate showcases their
                proficiency in React.js and front-end development, making it a
                valuable addition to their academic portfolio, resumes, and
                future college applications.
                <br />
                By mastering React.js, kids not only learn how modern web apps
                work but also gain the confidence to create their own, opening
                doors to advanced topics like Next.js, app development, and
                full-stack programming.
                <br />
                With a strong portfolio and an industry-recognized certificate,
                learners graduate from Codingal’s program ready to code, create,
                and innovate in the real world.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our React.js classes are appropriate for elementary & middle school students"
            description={
              <>
                At Codingal, our React.js classes are thoughtfully designed to
                make learning advanced web development fun, visual, and
                age-appropriate for elementary and middle school students
                (Grades 4–8).
                <br />
                Students begin by exploring HTML, CSS, and JavaScript to
                understand the structure, style, and logic behind web pages.
                Once they’re confident with these basics, they dive into
                React.js, learning how to use components, props, state, and
                events to build modern, interactive web applications.
                <br />
                Each project is designed to balance creativity with logic.
                Younger learners create simple yet fun web elements, like
                click-based animations, quizzes, and digital scoreboards, while
                older students work on full-fledged apps that include dynamic
                content, reusable components, and live updates.
                <br />
                Kids also explore how React renders HTML efficiently using its
                Virtual DOM, helping them understand how fast and dynamic
                websites like YouTube and Instagram work. With every class, they
                gain more control over how apps respond, update, and interact,
                building confidence with each line of code.
                <br />
                By the end of the course, students can proudly create their own
                interactive websites and web apps, proving that coding isn’t
                just about learning commands, but about turning imagination into
                innovation.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to React.Js for Kids</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={REACT_JS}
            title="Frequently Asked Questions (FAQs)"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={REACT_JS}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="React.Js for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default ReactjsPage
